import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, CheckCircle2, XCircle, ArrowRight, RotateCcw, Trophy, Clock, Target, Zap } from "lucide-react";
import { toast } from "sonner";

function fmt(sec) {
  const m = Math.floor(Math.max(0, sec) / 60).toString().padStart(2, "0");
  const s = Math.floor(Math.max(0, sec) % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

const deadlineKey = (userId, quizId) => `apm_quiz_deadline_${userId || "anon"}_${quizId}`;
const answersKey = (userId, quizId) => `apm_quiz_answers_${userId || "anon"}_${quizId}`;

export default function Quiz() {
  const { quizId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resumed, setResumed] = useState(false);

  // Timer
  const [timeLeft, setTimeLeft] = useState(null); // seconds
  const timerRef = useRef(null);
  const autoSubmitRef = useRef(false);

  // Drill-missed mode (client-only)
  const [drill, setDrill] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/curriculum/quiz/${quizId}`);
        setQuiz(data);

        // Restore saved answers (helps continuity across refresh on long final exam)
        try {
          const saved = JSON.parse(localStorage.getItem(answersKey(user?.id, quizId)) || "null");
          if (saved && typeof saved === "object") {
            setAnswers(saved);
          }
        } catch { /* localStorage unavailable; non-fatal */ }

        if (data.time_limit_minutes) {
          const dKey = deadlineKey(user?.id, quizId);
          const stored = parseInt(localStorage.getItem(dKey) || "0", 10);
          const now = Date.now();
          if (stored && stored > now) {
            // Resume existing deadline
            const remaining = Math.floor((stored - now) / 1000);
            setTimeLeft(remaining);
            setResumed(true);
          } else {
            const deadline = now + data.time_limit_minutes * 60 * 1000;
            localStorage.setItem(dKey, String(deadline));
            setTimeLeft(data.time_limit_minutes * 60);
          }
        }
      } catch (e) {
        // 403 = locked behind prerequisite (e.g. final exam needs M5-L1).
        // Send the user to FinalExamIntro for the final exam, otherwise dashboard.
        if (e?.response?.status === 403) {
          toast.error(e.response.data?.detail || "Locked — complete the prerequisite first.");
          if (quizId === "final-exam") {
            nav("/final-exam");
            return;
          }
        } else {
          toast.error("Quiz not found");
        }
        nav("/dashboard");
      }
    })();
  }, [quizId, nav, user?.id]);

  // Persist answers as user types
  useEffect(() => {
    if (!quiz || result) return;
    try {
      localStorage.setItem(answersKey(user?.id, quizId), JSON.stringify(answers));
    } catch { /* quota/private-mode; non-fatal */ }
  }, [answers, quiz, result, user?.id, quizId]);

  // Recompute timer from deadline (immune to background-tab throttling)
  useEffect(() => {
    if (!quiz || result || timeLeft === null) return;
    clearInterval(timerRef.current);
    const dKey = deadlineKey(user?.id, quizId);
    const deadline = parseInt(localStorage.getItem(dKey) || "0", 10);
    if (!deadline) return;

    timerRef.current = setInterval(() => {
      const remaining = Math.floor((deadline - Date.now()) / 1000);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        setTimeLeft(0);
        if (!autoSubmitRef.current) {
          autoSubmitRef.current = true;
          autoSubmit();
        }
      } else {
        setTimeLeft(remaining);
      }
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, [quiz, result, user?.id, quizId]);

  const setAnswer = (qIdx, optIdx) => {
    if (result) return;
    setAnswers((a) => ({ ...a, [qIdx]: optIdx }));
  };

  const finalizeAnswers = () => {
    const arr = [];
    for (let i = 0; i < quiz.questions.length; i++) {
      arr.push(answers[i] ?? -1);
    }
    return arr;
  };

  const clearTimerStorage = () => {
    try {
      localStorage.removeItem(deadlineKey(user?.id, quizId));
      localStorage.removeItem(answersKey(user?.id, quizId));
    } catch { /* non-fatal */ }
  };

  const submit = async () => {
    if (!quiz) return;
    const missing = [];
    for (let i = 0; i < quiz.questions.length; i++) {
      if (answers[i] === undefined) missing.push(i + 1);
    }
    if (missing.length) {
      toast.error(`Please answer question ${missing[0]}`);
      return;
    }
    await doSubmit(finalizeAnswers(), false);
  };

  const autoSubmit = async () => {
    if (!quiz || result || submitting) return;
    toast.warning("Time's up — submitting your quiz");
    await doSubmit(finalizeAnswers(), true);
  };

  const doSubmit = async (arr, isAuto) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/progress/quiz-submit", { quiz_id: quizId, answers: arr });
      setResult(data);
      clearInterval(timerRef.current);
      clearTimerStorage();
      toast.success(data.passed ? "Passed!" : isAuto ? "Auto-submitted. Review below." : "Review your answers below");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Couldn't submit quiz");
    } finally {
      setSubmitting(false);
    }
  };

  const retry = () => {
    setAnswers({});
    setResult(null);
    autoSubmitRef.current = false;
    clearTimerStorage();
    if (quiz?.time_limit_minutes) {
      const deadline = Date.now() + quiz.time_limit_minutes * 60 * 1000;
      localStorage.setItem(deadlineKey(user?.id, quizId), String(deadline));
      setTimeLeft(quiz.time_limit_minutes * 60);
    }
    setResumed(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --- Drill-missed mode ---
  const missedIndices = useMemo(
    () => (result?.per_question || []).filter((p) => !p.is_correct).map((p) => p.index),
    [result]
  );

  const startDrill = () => {
    if (!quiz || !missedIndices.length) return;
    const questions = missedIndices.map((i) => ({
      originalIndex: i,
      q: quiz.questions[i].q,
      options: quiz.questions[i].options,
      correctAnswer: result.per_question[i].correct_answer,
    }));
    setDrill({ questions, idx: 0, choice: null, reveal: false, correctCount: 0, total: questions.length });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pickDrill = (optIdx) => {
    if (!drill || drill.reveal) return;
    setDrill((d) => ({
      ...d,
      choice: optIdx,
      reveal: true,
      correctCount: d.correctCount + (optIdx === d.questions[d.idx].correctAnswer ? 1 : 0),
    }));
  };

  const nextDrill = () => {
    setDrill((d) => {
      if (d.idx + 1 >= d.questions.length) return { ...d, done: true };
      return { ...d, idx: d.idx + 1, choice: null, reveal: false };
    });
  };

  const exitDrill = () => setDrill(null);

  if (!quiz) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-20 text-zinc-500">Loading quiz…</div>
      </div>
    );
  }

  const isFinal = quiz.id === "final-exam";
  const answeredCount = Object.keys(answers).length;

  // ---- DRILL VIEW ----
  if (drill) {
    const done = drill.done || drill.idx >= drill.questions.length;
    if (done) {
      return (
        <div className="min-h-screen bg-white">
          <Navbar />
          <main className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="drill-complete">
            <Target className="w-12 h-12 text-red-600 mx-auto mb-5" />
            <h1 className="font-heading text-4xl font-bold tracking-tight">Drill complete</h1>
            <p className="text-zinc-600 mt-3">
              You got <span className="font-bold text-zinc-900">{drill.correctCount} of {drill.total}</span> correct in the missed-only drill.
            </p>
            <div className="flex gap-3 justify-center mt-8">
              <button onClick={exitDrill} className="btn-outline" data-testid="drill-exit-btn">
                Back to results
              </button>
              <button onClick={startDrill} className="btn-primary" data-testid="drill-restart-btn">
                <RotateCcw className="w-4 h-4" /> Drill again
              </button>
            </div>
          </main>
        </div>
      );
    }
    const cur = drill.questions[drill.idx];
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid="drill-page">
          <button onClick={exitDrill} className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6" data-testid="drill-back-btn">
            <ArrowLeft className="w-4 h-4" /> Exit drill
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="overline"><span className="red-bar" />Missed-only drill</div>
            <div className="font-mono text-xs text-zinc-500">{drill.idx + 1} / {drill.total}</div>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-6">Flash-card practice</h1>

          <div className="card-tech p-8" data-testid={`drill-question-${drill.idx}`}>
            <div className="font-mono text-xs text-red-600 uppercase tracking-widest mb-1">Question</div>
            <div className="font-heading text-lg font-bold mb-5 leading-snug">{cur.q}</div>
            <div className="space-y-2">
              {cur.options.map((opt, oi) => {
                let cls = "quiz-option w-full text-left";
                if (drill.reveal) {
                  if (oi === cur.correctAnswer) cls += " correct";
                  else if (oi === drill.choice) cls += " incorrect";
                } else if (drill.choice === oi) {
                  cls += " selected";
                }
                return (
                  <button key={`drill-${drill.idx}-opt-${oi}`} type="button" onClick={() => pickDrill(oi)} className={cls} data-testid={`drill-option-${oi}`}>
                    <span className="font-mono text-xs text-zinc-500 w-4">{String.fromCharCode(65 + oi)}.</span>
                    <span className="text-sm text-zinc-900 flex-1">{opt}</span>
                    {drill.reveal && oi === cur.correctAnswer && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                    {drill.reveal && oi === drill.choice && oi !== cur.correctAnswer && <XCircle className="w-4 h-4 text-red-600" />}
                  </button>
                );
              })}
            </div>

            {drill.reveal && (
              <div className="mt-6 pt-5 border-t border-zinc-200 flex justify-end">
                <button onClick={nextDrill} className="btn-primary" data-testid="drill-next-btn">
                  {drill.idx + 1 >= drill.total ? "Finish" : "Next question"} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  const timerWarn = timeLeft !== null && timeLeft <= 60;
  const timerDanger = timeLeft !== null && timeLeft <= 10;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12" data-testid={`quiz-page-${quizId}`}>
        <Link to={isFinal ? "/dashboard" : -1} className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6" data-testid="quiz-back-link">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        {/* Sticky timer + title */}
        <div className="sticky top-16 z-20 -mx-6 px-6 py-4 bg-white/90 backdrop-blur border-b border-zinc-200 flex items-center justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="overline">{isFinal ? "Final practice exam" : quiz.id === "s98-q" ? "FDNY S-98 Practice Quiz" : "Lesson quiz"}</div>
            <div className="font-heading text-lg font-bold tracking-tight mt-0.5" data-testid="quiz-title">{quiz.title}</div>
          </div>
          {timeLeft !== null && !result && (
            <div className="flex items-center gap-2">
              {resumed && (
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500" data-testid="quiz-resumed">
                  Resumed
                </span>
              )}
              <div
                className={`flex items-center gap-2 px-4 py-2 border-2 font-mono font-bold tabular-nums transition-colors ${
                  timerDanger ? "border-red-600 bg-red-50 text-red-700 animate-pulse"
                  : timerWarn ? "border-red-600 text-red-700"
                  : "border-zinc-300 text-zinc-900"
                }`}
                data-testid="quiz-timer"
              >
                <Clock className="w-4 h-4" /> {fmt(timeLeft)}
              </div>
            </div>
          )}
        </div>

        <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest mb-8">
          {quiz.questions.length} questions · {quiz.passing_score_pct || 70}% to pass
          {quiz.time_limit_minutes ? ` · ${quiz.time_limit_minutes} min limit` : ""}
        </div>

        {result && (
          <div className={`card-tech p-8 mb-10 ${result.passed ? "border-green-400" : "border-red-400"}`} data-testid="quiz-result-card">
            <div className="flex items-center gap-3 mb-3">
              {result.passed ? <Trophy className="w-7 h-7 text-green-600" /> : <XCircle className="w-7 h-7 text-red-600" />}
              <div className="font-heading text-3xl font-bold tracking-tight">
                {result.score}/{result.total} · {result.percentage}%
              </div>
            </div>
            <div className={`text-sm font-semibold ${result.passed ? "text-green-700" : "text-red-600"}`}>
              {result.passed ? "PASSED — great work." : "Not quite. Review incorrect answers below and try again."}
            </div>
            <div className="mt-5 flex gap-3 flex-wrap">
              <button onClick={retry} className="btn-outline" data-testid="quiz-retry-btn">
                <RotateCcw className="w-4 h-4" /> Try again
              </button>
              {missedIndices.length > 0 && (
                <button onClick={startDrill} className="btn-primary" data-testid="quiz-drill-missed-btn">
                  <Zap className="w-4 h-4" /> Drill missed ({missedIndices.length})
                </button>
              )}
              <Link to="/dashboard" data-testid="quiz-dashboard-btn">
                <button className="btn-outline">Dashboard <ArrowRight className="w-4 h-4" /></button>
              </Link>
            </div>
          </div>
        )}

        <ol className="space-y-8">
          {quiz.questions.map((q, qi) => {
            const given = answers[qi];
            const res = result?.per_question?.[qi];
            return (
              <li key={`${quiz.id}-q${qi}`} className="card-tech p-6" data-testid={`quiz-question-${qi}`}>
                <div className="font-mono text-xs text-red-600 uppercase tracking-widest mb-1">Question {qi + 1}</div>
                <div className="font-heading text-lg font-bold tracking-tight mb-4 leading-snug">{q.q}</div>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => {
                    let cls = "quiz-option w-full text-left";
                    if (!result && given === oi) cls += " selected";
                    if (result && res) {
                      if (oi === res.correct_answer) cls += " correct";
                      else if (oi === res.given_answer && !res.is_correct) cls += " incorrect";
                    }
                    return (
                      <button
                        type="button"
                        key={`${quiz.id}-q${qi}-opt${oi}`}
                        onClick={() => setAnswer(qi, oi)}
                        className={cls}
                        data-testid={`quiz-${qi}-option-${oi}`}
                      >
                        <span className="font-mono text-xs text-zinc-500 mt-0.5 w-4 flex-shrink-0">{String.fromCharCode(65 + oi)}.</span>
                        <span className="text-sm text-zinc-900 flex-1">{opt}</span>
                        {result && res && oi === res.correct_answer && <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />}
                        {result && res && oi === res.given_answer && !res.is_correct && <XCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />}
                      </button>
                    );
                  })}
                </div>
              </li>
            );
          })}
        </ol>

        {!result && (
          <div className="mt-10 pt-8 border-t border-zinc-200 flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm text-zinc-600 font-mono">
              {answeredCount} / {quiz.questions.length} answered
            </div>
            <button onClick={submit} disabled={submitting} className="btn-primary" data-testid="quiz-submit-btn">
              {submitting ? "Submitting…" : "Submit quiz"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
